<template lang="pug">
  .b-spec-photos(data-testid="photos-dialog")
    ._title Найти фото
    ._item(v-for="row in rows" :key="row.itemId" data-testid="photos-item")
      ._item-head
        span._item-name(data-testid="photos-item-name") {{ row.name }}
        label._upload
          | Загрузить своё
          input._file(type="file" accept="image/*" data-testid="photos-upload-input" @change="onUpload(row, $event)")
      ._uploaded(v-if="row.uploaded") фото загружено
      ._failed(v-if="row.failed" data-testid="photos-item-failed") не удалось найти, повторите позже
      ._grid(v-else)
        ._candidate(v-for="(candidate, i) in row.candidates" :key="i" :class="{ applied: row.appliedUrl === candidate.imageUrl }")
          img._img(
            data-testid="photos-candidate"
            :src="candidate.imageUrl"
            :alt="row.name"
            @click="apply(row, candidate)"
            )
          a._source(
            v-if="candidate.pageUrl"
            data-testid="photos-candidate-source"
            :href="candidate.pageUrl"
            target="_blank"
            rel="noopener noreferrer"
            ) источник
        ._empty(v-if="!row.candidates.length") кандидатов не найдено
    ._buttons
      Btn(grey data-testid="photos-close" @click="$emit('finish', applied)") Закрыть
</template>

<script>
import specAiJobApi from '@/api/specAiJob';
import model from '@/model/specification';
import { apiRun } from '@/model/apiRun';

/**
 * Окно «Найти фото» (ТЗ 6.2, 5.4): по каждой позиции сетка кандидатов с источником, клик по
 * кандидату применяет фото (сервер скачивает изображение сам), «Загрузить своё» — ручная замена
 * тем же путём, что и «Заменить» в ячейке строки (общая загрузка файла и привязка к позиции).
 * Позиции из ошибочных пачек помечены «не удалось найти, повторите позже».
 * Сгенерировано Claude.
 */
export default {
  name: 'SpecPhotosPopupWrappable',
  data() {
    return {
      job: null,
      items: [],
      rows: [],
      applied: false,
    };
  },
  methods: {
    init(params = {}) {
      this.job = params.job ?? null;
      this.items = params.items ?? [];
      this.rows = this.buildRows();
    },
    buildRows() {
      const results = this.job?.result?.items ?? [];
      const failedIds = model.failedItemIds(this.job);
      const requested = (this.job?.input?.itemIds ?? []).map(Number);
      const ids = requested.length
        ? requested
        : [...results.map((r) => Number(r.itemId)), ...failedIds];

      return ids.map((itemId) => {
        const result = results.find((r) => Number(r.itemId) === itemId);
        const item = this.items.find((i) => i.id === itemId);
        return {
          itemId,
          name: item?.name ?? `Позиция ${itemId}`,
          candidates: result?.candidates ?? [],
          failed: !result && failedIds.includes(itemId),
          appliedUrl: null,
          uploaded: false,
        };
      });
    },
    async apply(row, candidate) {
      const item = await apiRun(
        () => specAiJobApi.applyPhoto(this.job.id, {
          itemId: row.itemId,
          imageUrl: candidate.imageUrl,
        }),
        'Не удалось применить фото',
      );
      if (!item) return;
      this.applied = true;
      row.appliedUrl = candidate.imageUrl; // eslint-disable-line no-param-reassign
    },
    /** «Загрузить своё»: общая загрузка файла с назначением «спецификация» и привязка (ТЗ 9.1). */
    async onUpload(row, event) {
      const [file] = Array.from(event.target.files ?? []);
      if (!file) return;
      const item = await model.uploadPhoto(row.itemId, file);
      if (!item) return;
      this.applied = true;
      row.uploaded = true; // eslint-disable-line no-param-reassign
    },
  },
};
</script>

<style scoped lang="scss">
.spec-photos {
  width: 720px;
  max-width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__title {
    font-size: 24px;
    font-weight: 700;
  }

  &__item-head {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 8px;
  }

  &__item-name {
    font-weight: 700;
  }

  &__upload {
    color: var(--color-accent);
    cursor: pointer;
    font-size: 13px;
  }

  &__file {
    position: absolute;
    opacity: 0;
    visibility: hidden;
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__candidate {
    width: 120px;
    border: 2px solid transparent;
    border-radius: 6px;
    padding: 2px;

    &.applied {
      border-color: var(--color-green);
    }
  }

  &__img {
    display: block;
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    background: #F8F9FC;
  }

  &__source {
    font-size: 11px;
    color: var(--color-accent);
  }

  &__failed,
  &__empty {
    color: var(--color-middle-gray);
    font-size: 13px;
  }

  &__uploaded {
    color: var(--color-green);
    font-size: 13px;
  }

  &__buttons {
    display: flex;
    gap: 12px;
  }
}
</style>
